import React from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { Moon, Sun, Settings, LogOut, Menu, ShieldCheck } from 'lucide-react'
import { useDarkMode } from '../../context/DarkModeContext'

interface HeaderProps {
  title: string
  onLogout: () => void
  showMenuButton?: boolean
  onMenuToggle?: () => void
}

export const Header: React.FC<HeaderProps> = ({ title, onLogout, showMenuButton, onMenuToggle }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { isDarkMode, toggleDarkMode } = useDarkMode()

  return (
    <header className="app-header sticky top-0 z-30 w-full frosted transition-colors duration-300">
      <div className="flex items-center justify-between h-[68px] sm:h-[76px] max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex items-center gap-3 sm:gap-5 min-w-0">
          {showMenuButton && (
            <button
              onClick={onMenuToggle}
              className="icon-button md:hidden -ml-1"
              aria-label="Menu openen"
            >
              <Menu size={20} />
            </button>
          )}

          <button
            type="button"
            onClick={() => navigate(user?.role ? `/dashboard/${user.role}` : '/')}
            className="hidden xs:flex items-center gap-3 pr-4 sm:pr-5 border-r"
            style={{ borderColor: 'var(--border)' }}
            aria-label="Naar dashboard"
          >
            <img src="/assets/logo.png" alt="KVH" className="w-[72px] sm:w-[82px] h-auto" />
          </button>

          <div className="min-w-0 flex items-center gap-3">
            <div className="min-w-0">
              <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.16em] mb-0.5" style={{ color: 'var(--text-tertiary)' }}>
                KVH Operations
              </p>
              <h1 className="font-semibold text-[15px] sm:text-base truncate leading-tight" style={{ color: 'var(--text-primary)' }}>{title}</h1>
            </div>
          </div>
        </div>

        <nav className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={toggleDarkMode}
            className="icon-button"
            title={isDarkMode ? 'Lichte modus' : 'Donkere modus'}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {user?.role === 'admin' && (
            <button
              onClick={() => navigate('/settings')}
              className="icon-button"
              title="Instellingen"
            >
              <Settings size={18} />
            </button>
          )}

          {user && (
            <div className="hidden md:flex items-center gap-3 ml-1 pl-3 border-l" style={{ borderColor: 'var(--border)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-sm" style={{ background: 'var(--kvh-red-alpha)', color: 'var(--kvh-red)' }}>
                {user.name?.trim()?.charAt(0)?.toUpperCase() || <ShieldCheck size={17} />}
              </div>
              <div className="leading-tight max-w-[150px]">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{user.name}</p>
                <p className="text-[11px] capitalize" style={{ color: 'var(--text-tertiary)' }}>{user.role}</p>
              </div>
            </div>
          )}

          <button
            onClick={onLogout}
            className="btn-ghost ml-0 sm:ml-1"
            title="Uitloggen"
          >
            <LogOut size={16} />
            <span className="hidden lg:inline">Uitloggen</span>
          </button>
        </nav>
      </div>
    </header>
  )
}
